import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { refreshAccessToken } from './axiosInstance';
import { setAccessToken, clearAccessToken } from './tokenStore';
import api from './axiosInstance';

const AuthContext = createContext(null);

/**
 * Provides authentication state to the entire React tree.
 *
 * On mount it attempts a silent token refresh using the HttpOnly
 * refreshToken cookie.  If the cookie is valid, the user is restored
 * without any login prompt (survives page refreshes).  If not, the
 * user is treated as logged-out.
 *
 * Access token lives in the tokenStore module (memory only — never
 * in localStorage or a JS-readable cookie).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // isInitialized gates routing: ProtectedRoute must not redirect
  // before the silent refresh attempt completes.
  const [isInitialized, setIsInitialized] = useState(false);

  const initialize = useCallback(async () => {
    try {
      const res = await refreshAccessToken();
      setAccessToken(res.data.token);
      setUser(res.data.user);
    } catch {
      // No valid refresh token — user is not authenticated
      clearAccessToken();
    } finally {
      setIsInitialized(true);
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback((token, userData) => {
    setAccessToken(token);
    setUser(userData);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/logout');
    } catch {
      // Server-side invalidation best-effort; always clear client state
    }
    clearAccessToken();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isInitialized,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
